import { AxiosError, AxiosResponse } from "axios";

import SystemError, { HitomeqErrorType } from "@/commons/models/SystemError";
import RequestHeaderUtil from "@/commons/utils/RequestHeader";
import LoginUser from "@/store/LoginUser";
import { RestUrl } from "@/commons/constants/RestUrl";
import { LoginByFacilityResponse } from "@/commons/services/Authorization";

/**
 * ### 共通Serviceクラス
 */
export default class CommonService {
  /** リトライ回数 */
  readonly MAX_RETRY_COUNT = 3;

  /**
   * ### 取得実処理
   *
   * - axiosを使用してAPIにgetリクエストを送る
   * - 認証情報無しのGET
   *
   * @param url リクエストURL
   * @param params リクエストに付与するパスパラメーター
   * @param className クラス名
   */
  public async executeSimpleGet<T>(url: string, className: string, params: any = {}, errorType: HitomeqErrorType, responseType?: string): Promise<AxiosResponse<T>> {
    let response: AxiosResponse<T> = {} as any;

    try {
      // 取得処理実施
      response = await window.axios.get(url, {
        headers: RequestHeaderUtil.createHeader(),
        params: params,
        responseType: responseType ? (responseType as any) : undefined
      });

      // ステータスコードをチェックしてエラーなら例外を投げる
      this.checkStatusCode(null, response, className, "get", null, errorType);
    } catch (error) {
      const axiosError: AxiosError = error as AxiosError;
      console.error("axiosError:", axiosError);

      // ステータスコードをチェックしてエラーなら例外を投げる
      this.checkStatusCode(axiosError, axiosError.response as AxiosResponse, className, "get", null, errorType);
    }

    return response;
  }

  public async executeSimpleNoCorsGet<T>(url: string, className: string, params: any = {}, errorType: HitomeqErrorType, responseType?: string): Promise<AxiosResponse<T>> {
    let response: AxiosResponse<T> = {} as any;

    try {
      // 取得処理実施
      response = await window.axios.get(url, {
        headers: RequestHeaderUtil.createHeader(),
        params: params,
        responseType: responseType ? (responseType as any) : undefined,
        mode: "no-cors"
      } as any);

      // ステータスコードをチェックしてエラーなら例外を投げる
      this.checkStatusCode(null, response, className, "get", null, errorType);
    } catch (error) {
      const axiosError: AxiosError = error as AxiosError;
      console.error("axiosError:", axiosError);

      // ステータスコードをチェックしてエラーなら例外を投げる
      this.checkStatusCode(axiosError, axiosError.response as AxiosResponse, className, "get", null, errorType);
    }

    return response;
  }

  /**
   * ### 取得実処理
   *
   * - axiosを使用してAPIにgetリクエストを送る
   * - 認証情報の有効期限切れの場合は認証情報更新を試みる
   *   - 認証情報が更新できた場合はstoreを更新し、リトライする
   *   - 認証情報が更新できなかった場合は例外を投げて再ログインを促す
   *
   * @param userStore ログインユーザー情報のstore
   * @param url リクエストURL
   * @param params リクエストに付与するパスパラメーター
   * @param className クラス名
   * @param retryCount 現在のリトライ数
   */
  public async executeGet<T>(userStore: LoginUser, url: string, className: string, params: any = {}, errorType: HitomeqErrorType, retryCount: number): Promise<AxiosResponse<T>> {
    try {
      // 取得処理実施
      await this.preprocessing(userStore, className);
      return await window.axios.get(url, {
        headers: RequestHeaderUtil.createAuthorizationHeader(userStore.authorization),
        params: params
      });
    } catch (error) {
      const axiosError: AxiosError = error as AxiosError;
      console.error("axiosError:", axiosError);

      // 3回までリトライする
      const needRetry: boolean = this.needRetry(axiosError.response as AxiosResponse);
      if (retryCount < this.MAX_RETRY_COUNT && needRetry) {
        console.debug(`commonService::executeGet() className:${className}`, "retryCount=", retryCount, "needRetry():", needRetry);
        // 取得処理リトライ
        retryCount++;
        return await this.executeGet(userStore, url, className, params, errorType, retryCount);
      } else {
        // ステータスコードをチェックしてエラーなら例外を投げる
        this.checkStatusCode(axiosError, axiosError.response as AxiosResponse, className, "get", null, errorType);
      }
    }
    return {} as any;
  }

  /**
   * ### 送信実処理
   *
   * - axiosを使用してAPIにgetリクエストを送る
   * - 認証情報無しのPOST
   *
   * @param url リクエストURL
   * @param params リクエストに付与するパスパラメーター
   * @param className クラス名
   */
  public async executeSimplePost<T>(url: string, requestBody: any, className: string, params: any = {}, errorType: HitomeqErrorType): Promise<AxiosResponse<T>> {
    try {
      return await window.axios.post(url, JSON.stringify(requestBody), {
        headers: RequestHeaderUtil.createHeader(),
        params: params
      });
    } catch (error) {
      const axiosError: AxiosError = error as AxiosError;
      console.error("axiosError:", axiosError);

      // ステータスコードをチェックしてエラーなら例外を投げる
      this.checkStatusCode(axiosError, axiosError.response as AxiosResponse, className, "post", null, errorType);
    }
    return {} as any;
  }

  /**
   * ### 追加実処理
   *
   * - axiosを使用してAPIにpostリクエストを送る
   * - 認証情報の有効期限切れの場合は認証情報更新を試みる
   *   - 認証情報が更新できた場合はstoreを更新し、リトライする
   *   - 認証情報が更新できなかった場合は例外を投げて再ログインを促す
   *
   * @param userStore ログインユーザー情報のstore
   * @param url リクエストURL
   * @param body リクエストボディ
   * @param className クラス名
   * @param retryCount 現在のリトライ数
   */
  public async executePost<T>(userStore: LoginUser, url: string, requestBody: any, className: string, errorType: HitomeqErrorType, retryCount: number): Promise<AxiosResponse<T>> {
    try {
      // 追加処理実施
      await this.preprocessing(userStore, className);
      return await window.axios.post(url, JSON.stringify(requestBody), {
        headers: RequestHeaderUtil.createAuthorizationHeader(userStore.authorization)
      });
    } catch (error) {
      const axiosError: AxiosError = error as AxiosError;
      console.error("axiosError:", axiosError);

      // 3回までリトライする
      const needRetry: boolean = this.needRetry(axiosError.response as AxiosResponse);
      if (retryCount < this.MAX_RETRY_COUNT && needRetry) {
        console.debug(`commonService::executeGet() className:${className}`, "retryCount=", retryCount, "needRetry():", needRetry);
        // 取得処理リトライ
        retryCount++;
        return await this.executePost(userStore, url, requestBody, className, errorType, retryCount);
      } else {
        // ステータスコードをチェックしてエラーなら例外を投げる
        this.checkStatusCode(axiosError, axiosError.response as AxiosResponse, className, "post", requestBody, errorType);
      }
    }
    return {} as any;
  }

  /**
   * ### 更新実処理
   *
   * - axiosを使用してAPIにpatchリクエストを送る
   * - 認証情報の有効期限切れの場合は認証情報更新を試みる
   *   - 認証情報が更新できた場合はstoreを更新し、リトライする
   *   - 認証情報が更新できなかった場合は例外を投げて再ログインを促す
   *
   * @param userStore ログインユーザー情報のstore
   * @param url リクエストURL
   * @param body リクエストボディ
   * @param className クラス名
   * @param retryCount 現在のリトライ数
   */
  public async executePatch<T>(userStore: LoginUser, url: string, requestBody: any, className: string, errorType: HitomeqErrorType, retryCount: number): Promise<AxiosResponse<T>> {
    try {
      // 更新処理実施
      await this.preprocessing(userStore, className);
      return await window.axios.patch(url, JSON.stringify(requestBody), {
        headers: RequestHeaderUtil.createAuthorizationHeader(userStore.authorization)
      });
    } catch (error) {
      const axiosError: AxiosError = error as AxiosError;
      console.error("axiosError:", axiosError);

      // 3回までリトライする
      const needRetry: boolean = this.needRetry(axiosError.response as AxiosResponse);
      if (retryCount < this.MAX_RETRY_COUNT && needRetry) {
        console.debug(`commonService::executeGet() className:${className}`, "retryCount=", retryCount, "needRetry():", needRetry);
        // 取得処理リトライ
        retryCount++;
        return await this.executePatch(userStore, url, requestBody, className, errorType, retryCount);
      } else {
        // ステータスコードをチェックしてエラーなら例外を投げる
        this.checkStatusCode(axiosError, axiosError.response as AxiosResponse, className, "patch", requestBody, errorType);
      }
    }
    return {} as any;
  }

  /**
   * ### 削除実処理
   *
   * - axiosを使用してAPIにdeleteリクエストを送る
   * - 認証情報の有効期限切れの場合は認証情報更新を試みる
   *   - 認証情報が更新できた場合はstoreを更新し、リトライする
   *   - 認証情報が更新できなかった場合は例外を投げて再ログインを促す
   *
   * @param userStore ログインユーザー情報のstore
   * @param url リクエストURL
   * @param className クラス名
   * @param retryCount 現在のリトライ数
   */
  public async executeDelete<T>(
    userStore: LoginUser,
    url: string,
    className: string,
    params: any = {},
    errorType: HitomeqErrorType,
    retryCount: number,
    requestBody?: any
  ): Promise<AxiosResponse<T>> {
    try {
      // 削除処理実施
      await this.preprocessing(userStore, className);
      return await window.axios.delete(url, {
        headers: RequestHeaderUtil.createAuthorizationHeader(userStore.authorization),
        params: params,
        data: requestBody
      });
    } catch (error) {
      const axiosError: AxiosError = error as AxiosError;
      console.error("axiosError:", axiosError);

      // 3回までリトライする
      const needRetry: boolean = this.needRetry(axiosError.response as AxiosResponse);
      if (retryCount < this.MAX_RETRY_COUNT && needRetry) {
        console.debug(`commonService::executeGet() className:${className}`, "retryCount=", retryCount, "needRetry():", needRetry);
        // 取得処理リトライ
        retryCount++;
        return await this.executeDelete(userStore, url, className, params, errorType, retryCount);
      } else {
        // ステータスコードをチェックしてエラーなら例外を投げる
        this.checkStatusCode(axiosError, axiosError.response as AxiosResponse, className, "delete", requestBody, errorType);
      }
    }
    return {} as any;
  }

  /**
   * ### 通信前処理
   *
   * APIへのリクエストが可能か確認する
   *
   * - 認証情報が存在するか確認する
   * - 認証トークンの有効期限切れを確認して、必要なら認証トークンを更新する
   *
   * @param userStore
   * @param className
   * @param method
   */
  private async preprocessing(userStore: LoginUser, className: string): Promise<void> {
    // 有効期限を確認して、有効期限切れならトークンを更新する
    const expiration: Date = new Date(userStore.authorization.expiration);
    if (this.isExpired(expiration)) {
      await this.refreshToken(userStore, className);
    }
    return;
  }

  /**
   * ### 有効期限を確認する
   *
   * - 現在日時と比較して、有効期限切れかを判断する
   *
   * @param expiration 有効期限
   * @returns 有効期限切れならtrue
   */
  protected isExpired(expiration: Date): boolean {
    const expirationTime: number = expiration.getTime();
    const nowTime: number = new Date().getTime();
    if (expirationTime <= nowTime) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * ### 認証情報のRefresh
   *
   * - 認証情報の更新リクエストをAPIに投げる
   * - 更新結果が成功であれば更新した認証情報でstoreを更新する
   * - 更新結果が成功以外であれば、checkStatusCode()が例外を投げる
   *
   * @param userStore ログインユーザー情報のstore
   * @param className クラス名
   */
  private async refreshToken(userStore: LoginUser, className: string): Promise<void> {
    console.debug("token refresh.");

    const requestBody: any = {
      userId: userStore.loginUser.userId,
      facilityId: userStore.loginUser.facilityId,
      refreshToken: userStore.authorization.refreshToken
    };

    const response: AxiosResponse<LoginByFacilityResponse> = await window.axios.post(RestUrl.Refresh, JSON.stringify(requestBody), {
      headers: RequestHeaderUtil.createHeader()
    });

    this.checkStatusCode(null, response, className, "refreshToken", requestBody, HitomeqErrorType.SYSTEM_ERROR);

    // 取得した新しい認証情報でstoreを更新する
    userStore.updateAuthorization({ authorization: response.data.authorization });
    userStore.updateCredentials({ credentials: response.data.credentials });

    return;
  }

  /**
   * ### レスポンスのステータスコードからリトライが必要かを返す
   */
  protected needRetry(response: AxiosResponse<any>): boolean {
    // ステータスがない場合（多分ネットワークエラー？）は念のためリトライする
    if (!response || !response.status) {
      return true;
    }

    // リトライ不要で即エラー扱いのステータス
    switch (response.status) {
      case 400:
      case 401:
      case 409:
      case 404:
      case 422:
      case 413: {
        console.debug(`status: ${response.status}. no need to retry`);
        return false;
      }
    }

    // その他200以外はリトライする
    if (response.status != 200) {
      return true;
    } else {
      return false;
    }
  }

  /**
   * ### ステータスコードチェック
   *
   * - ステータスコードからエラーかどうか判断する
   * - エラーの場合は例外を投げる
   *
   * @param response axiosレスポンス
   * @param className クラス名
   * @param method メソッド名
   */
  public checkStatusCode(
    axiosError: AxiosError | null,
    response: AxiosResponse<any>,
    className: string,
    method: string,
    requestBody: Object | null,
    errorType: HitomeqErrorType
  ): void {
    // レスポンスが存在しない場合は固有の例外を投げる
    if (axiosError && !axiosError.response) {
      throw this.createNoResponseException(axiosError, className, method, requestBody, errorType);
    }

    // ステータスコード200番台はエラーではないので先にチェックする
    if (200 <= response.status && response.status < 300) {
      return;
    }

    console.error("CommonService::checkStatusCode() invalid status:", response.status);

    switch (response.status) {
      case 400:
        throw this.createException(axiosError, response, className, method, requestBody, "bad request", errorType);
      case 401:
        throw this.createException(axiosError, response, className, method, requestBody, "unauthorized", errorType);
      case 403:
        throw this.createException(axiosError, response, className, method, requestBody, "expired token", errorType);
      case 404:
        throw this.createException(axiosError, response, className, method, requestBody, "not found", errorType);
      case 405:
        throw this.createException(axiosError, response, className, method, requestBody, "method not allowed", errorType);
      case 409:
        throw this.createException(axiosError, response, className, method, requestBody, "confricted", errorType);
      case 413:
        throw this.createException(axiosError, response, className, method, requestBody, "request too large", errorType);
      case 422:
        throw this.createException(axiosError, response, className, method, requestBody, "Unprocessable Entity", errorType);
      case 500:
        throw this.createException(axiosError, response, className, method, requestBody, "api system error", errorType);
      case 502:
        throw this.createException(axiosError, response, className, method, requestBody, "lambda is not found", errorType);
      case 503:
        throw this.createException(axiosError, response, className, method, requestBody, "system maintenance", errorType);
      case 504:
        throw this.createException(axiosError, response, className, method, requestBody, "api time out", errorType);
      default:
        throw this.createException(axiosError, response, className, method, requestBody, "unexpected exception", errorType);
    }
  }

  /**
   * ### 共通例外を作成する
   *
   * - 自クラス名とrequestの組み合わせでどのAPIが実行されたかは特定可能
   * - 発生個所が分かるように、自クラス名をmessageとして格納する
   * - 呼び出し元が追加でメッセージは指定可能(optional)
   * - axiosレスポンスの内容を格納する
   *
   * @param response axiosレスポンス
   * @param className クラス名
   * @param method メソッド名
   * @param message メッセージ
   */
  protected createException(
    axiosError: AxiosError | null,
    response: AxiosResponse<any>,
    className: string,
    method: string,
    requestBody: Object | null,
    message: string = "",
    errorType: HitomeqErrorType
  ): SystemError {
    const error: SystemError = new SystemError(`[${className}] ${method}(), ${message}`);

    // axios error から格納
    error.code = axiosError ? axiosError.code : "";
    // エラーメッセージがネットワークエラーの場合はそれをそのまま使用する
    error.message = axiosError ? axiosError.message + `\n[${className}] ${method}(), ${message}` : `[${className}] ${method}(), ${message}`;

    // axios responseから格納
    error.statusCode = response.status;
    error.headers = response.headers;
    error.request = response.request;
    error.statusText = response.statusText;
    error.originalResponse = response.request.response;
    error.apiUrl = response.config.url ? response.config.url : response.request.responseURL;

    // アプリで残したい情報を格納
    error.method = message;
    error.requestBody = requestBody;
    error.hitomeqErrorType = errorType;

    console.log("createException() error", JSON.parse(JSON.stringify(error)));

    return error;
  }

  /**
   * ### レスポンスがない場合の例外を作成する
   */
  protected createNoResponseException(axiosError: AxiosError | null, className: string, method: string, requestBody: Object | null, errorType: HitomeqErrorType): SystemError {
    const error: SystemError = new SystemError(`[${className}] ${method}(), no response error`);
    // ネットワークエラーとして扱うためのコードを格納する
    error.code = "ERR_NETWORK";
    error.message = axiosError ? axiosError.message + `\n[${className}] ${method}(), no response error` : `[${className}] ${method}(), no response error`;

    // アプリで残したい情報を格納
    error.method = axiosError ? axiosError.message + "no response error" : "no response error";
    error.requestBody = requestBody;
    error.hitomeqErrorType = errorType;
    return error;
  }
}
